/** Hosted-page OAuth for the Watchtower Console (SIWE happens on Guardius's
 *  /auth/authorize page). The SPA owns only the public-client legs: PKCE,
 *  redirect, and code exchange — no client secret ever lives here.
 *
 *  Access + refresh tokens live in sessionStorage: never localStorage, cleared
 *  on tab close. This survives a page refresh without a full re-auth round-trip
 *  while keeping the tokens out of long-lived storage.
 *
 *  Watchtower tokens must be minted for the Guardius MANAGEMENT tenant so the
 *  token's `aud` matches what the Watchtower Lambdas require. The console
 *  therefore always supplies a concrete `tenant_id` from runtime config to the
 *  authorize URL. All values (API base, Guardius base, tenant) come from
 *  deploy-time runtime config.json. */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  buildAuthorizeUrl,
  decodeJwtClaims,
  generatePkce,
  permissionMatches,
  randomState,
  PLATFORM_ID,
  READ_PERMISSION,
} from './oauth';

export interface RuntimeConfig {
  apiUrl: string;
  guardiusUrl: string;
  /** The Guardius management tenant UUID. Supplied to the authorize URL so the
   *  minted token's `aud` matches what the Watchtower Lambdas require. */
  tenantId: string;
  env: string;
}

export interface AuthContextValue {
  config: RuntimeConfig;
  token: string | null;
  address: string | null;
  /** Client-side read gate: true when the token carries the usage:read
   *  permission (or its permissions list is truncated — permissive, since the
   *  Lambdas' Guardius authorizer is authoritative and will 403 an unauthorized
   *  read). UI gating only. */
  canRead: boolean;
  /** Set true when a data-fetch is refused by the server with a 403 (the API
   *  authorizer is authoritative and can deny a read the client-side `canRead`
   *  gate let through). The Gate renders NoAccess while this is set. Reset by
   *  `signOut()` so a fresh login re-evaluates. */
  serverDenied: boolean;
  /** Flip `serverDenied` on — called by the API layer when a fetch 403s. */
  denyAccess(): void;
  /** Callback / exchange error surfaced for the sign-in UI to render. */
  error: string | null;
  signIn(): Promise<void>;
  signOut(): void;
  /** Return a still-valid access token for the next API request, refreshing
   *  silently if the current one is missing or about to expire (within
   *  EXPIRY_SKEW_MS). Resolves to `null` when the session is dead (no refresh
   *  token, or the refresh itself failed → user is signed out). The API client
   *  awaits this before attaching the `Authorization` header. */
  ensureFreshToken(): Promise<string | null>;
}

// sessionStorage keys. `watchtower.oauth.pending` carries the one-shot PKCE
// verifier + CSRF state + redirect URI, written by signIn() and consumed
// exactly once by the boot callback. `watchtower.token` holds the access token.
// `watchtower.refresh` holds the rotating refresh token (same posture: never
// localStorage, cleared on tab close). Persisted on every grant — the
// authorization_code exchange and each refresh rotation — and consumed by the
// single-flight `refreshSession`. Guardius rotates this on every use with
// reuse-detection, so the OLD value MUST be overwritten the moment a new one
// arrives (a second use of a spent token revokes ALL the user's sessions).
const PENDING_KEY = 'watchtower.oauth.pending';
const TOKEN_KEY = 'watchtower.token';
const REFRESH_KEY = 'watchtower.refresh';

// Refresh the access token this far ahead of its `exp`. Covers clock skew plus
// the round-trip, so a request never carries a token that expires in-flight. A
// background-throttled timer or a slept laptop can blow past this; the
// visibility-change top-up and the per-request `ensureFreshToken` recover those.
export const EXPIRY_SKEW_MS = 60_000;

// The OAuth callback path on this origin. The SPA is served at the site root.
const redirectPath = '/';

interface PendingAuth {
  verifier: string;
  state: string;
  redirectUri: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Failure kinds for runtime-config loading. The boot sequence (App.tsx) keys
 *  on these to decide between a local-dev fallback and a loud error screen:
 *
 *    - 'not-found' : HTTP 404 — config genuinely absent (a real deploy always
 *                    ships config.json, so 404 means we're in local dev).
 *    - 'http'      : any other non-2xx (403/500/…) — a deployed config that the
 *                    origin refused or failed to serve. Must surface loudly.
 *    - 'parse'     : 2xx but the body isn't valid JSON. `contentType` carries the
 *                    response's Content-Type so callers can disambiguate an
 *                    SPA-fallback index.html (text/html) from a corrupt
 *                    application/json upload.
 *    - 'network'   : the fetch() itself rejected (DNS/offline/CORS). Ambiguous;
 *                    surfaced loudly — a same-origin asset fetch shouldn't reject. */
export type ConfigErrorKind = 'not-found' | 'http' | 'parse' | 'network';

export class ConfigError extends Error {
  constructor(
    public readonly kind: ConfigErrorKind,
    message: string,
    public readonly status?: number,
    public readonly contentType?: string | null,
  ) {
    super(message);
    this.name = 'ConfigError';
  }
}

/** Load deploy-time runtime config injected by the DashboardStack
 *  BucketDeployment (CDK Source.jsonData → /config.json). Throws a typed
 *  {@link ConfigError} carrying the failure kind so the boot sequence can decide
 *  between the local-dev fallback and a loud error screen. */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  let res: Response;
  try {
    res = await fetch('/config.json');
  } catch (e) {
    throw new ConfigError(
      'network',
      `config.json fetch failed: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  if (!res.ok) {
    const kind: ConfigErrorKind = res.status === 404 ? 'not-found' : 'http';
    throw new ConfigError(kind, `config.json load failed: ${res.status}`, res.status);
  }

  try {
    return (await res.json()) as RuntimeConfig;
  } catch (e) {
    throw new ConfigError(
      'parse',
      `config.json is not valid JSON: ${e instanceof Error ? e.message : String(e)}`,
      res.status,
      res.headers.get('content-type'),
    );
  }
}

/** Derive UI-gating fields from a stored token. Returns nulls / false when the
 *  token is absent, malformed, or expired (expired tokens are cleared by the
 *  caller). `canRead` is permissive when permissions are truncated: the server
 *  is authoritative and will 403 an unauthorized read, so we never hide a view
 *  the server might allow. */
export function deriveState(token: string | null): {
  address: string | null;
  canRead: boolean;
  expired: boolean;
} {
  if (!token) return { address: null, canRead: false, expired: false };
  const claims = decodeJwtClaims(token);
  if (!claims) return { address: null, canRead: false, expired: true };
  if (typeof claims.exp === 'number' && claims.exp * 1000 <= Date.now()) {
    return { address: null, canRead: false, expired: true };
  }
  const address = claims.wallet_address ?? claims.sub;
  const canRead =
    claims.permissions_truncated === true
      ? true
      : permissionMatches(claims.permissions, READ_PERMISSION);
  return { address, canRead, expired: false };
}

export function AuthProvider({
  config,
  children,
}: {
  config: RuntimeConfig;
  children: ReactNode;
}) {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serverDenied, setServerDenied] = useState(false);

  // One-shot guard for the OAuth callback exchange. Under React StrictMode the
  // boot effect is invoked twice; the first invoke consumes the one-shot pending
  // stash and starts the code exchange, so the second invoke MUST NOT re-enter
  // the callback path (the stash is gone — re-entry would spuriously reject as a
  // state mismatch and clobber a successful sign-in). This ref survives the
  // double-invoke (a module-level flag would leak across remounts/tests, so we
  // scope it per-provider). Because the stash is single-use, a second exchange
  // is impossible, so the resolved token is committed to state regardless of the
  // effect-cleanup `cancelled` flag.
  const exchangeStarted = useRef(false);

  // Single-flight guard for `refreshSession`. Holds the one in-flight refresh
  // promise so the proactive timer, the visibility top-up, and per-request
  // `ensureFreshToken` calls all share it — two concurrent refreshes would each
  // POST the same rotating refresh token, and the second use trips Guardius's
  // reuse-detection and revokes EVERY session for the user.
  const refreshing = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      // OAuth error bounce-back: Guardius redirects here with `?error=...` (and
      // no code) when sign-in itself fails.
      const oauthError = params.get('error');
      if (oauthError && !code) {
        stripQuery();
        if (!cancelled) setError('Sign-in failed: ' + oauthError);
        return;
      }

      if (code && state) {
        // Run the callback exchange exactly once across StrictMode's double
        // invoke. The very first invoke that reaches here owns the exchange.
        if (exchangeStarted.current) return;
        exchangeStarted.current = true;

        // Read-and-remove the one-shot pending stash. A missing stash or a
        // state mismatch is a hard reject — we must NOT exchange the code.
        const raw = sessionStorage.getItem(PENDING_KEY);
        sessionStorage.removeItem(PENDING_KEY);
        stripQuery();

        let pending: PendingAuth | null = null;
        try {
          pending = raw ? (JSON.parse(raw) as PendingAuth) : null;
        } catch {
          pending = null;
        }

        if (!pending || pending.state !== state) {
          setError('Sign-in could not be verified (state mismatch). Please try again.');
          return;
        }

        try {
          const res = await fetch(`${config.guardiusUrl}/auth/token`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              grant_type: 'authorization_code',
              code,
              redirect_uri: pending.redirectUri,
              code_verifier: pending.verifier,
              platform_id: PLATFORM_ID,
            }),
          });
          if (!res.ok) {
            setError(`Token exchange failed (${res.status}).`);
            return;
          }
          const body = (await res.json()) as {
            access_token?: string;
            refresh_token?: string;
          };
          if (!body.access_token) {
            setError('Token exchange returned no access token.');
            return;
          }
          // Silent refresh: persist the rotating refresh_token so the proactive
          // timer / per-request `ensureFreshToken` can keep this session alive
          // past the access token's lifetime without a re-sign-in.
          sessionStorage.setItem(TOKEN_KEY, body.access_token);
          if (body.refresh_token) sessionStorage.setItem(REFRESH_KEY, body.refresh_token);
          // Commit regardless of `cancelled`: the exchange is one-shot, so the
          // StrictMode cleanup that flipped `cancelled` is followed by a second
          // invoke that no-ops on the guard above — committing here is the only
          // path that lands the token in state.
          setToken(body.access_token);
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Token exchange failed.');
        }
        return;
      }

      // No callback: load any persisted token, clearing it if expired.
      const stored = sessionStorage.getItem(TOKEN_KEY);
      if (stored) {
        const { expired } = deriveState(stored);
        if (expired) {
          sessionStorage.removeItem(TOKEN_KEY);
          if (!cancelled) setToken(null);
        } else if (!cancelled) {
          setToken(stored);
        }
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [config]);

  // Single-flight refresh-token rotation. Returns the new access token, or null
  // on a dead session (no refresh token / failed refresh → signed out). All
  // callers funnel through the `refreshing` ref so only one POST is ever in
  // flight against the rotating refresh token.
  const refreshSession = (): Promise<string | null> => {
    if (refreshing.current) return refreshing.current;

    const run = async (): Promise<string | null> => {
      const refreshToken = sessionStorage.getItem(REFRESH_KEY);
      if (!refreshToken) return null;

      const killSession = (): null => {
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_KEY);
        setToken(null);
        return null;
      };

      try {
        const res = await fetch(`${config.guardiusUrl}/auth/token`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: refreshToken }),
        });
        if (!res.ok) return killSession();
        const body = (await res.json()) as { access_token?: string; refresh_token?: string };
        if (!body.access_token) return killSession();
        // Rotation: persist the NEW refresh token (and overwrite the spent one)
        // BEFORE anything else can read it. A reused refresh token revokes all
        // sessions, so the old value must never survive a successful refresh.
        sessionStorage.setItem(TOKEN_KEY, body.access_token);
        if (body.refresh_token) sessionStorage.setItem(REFRESH_KEY, body.refresh_token);
        setToken(body.access_token);
        return body.access_token;
      } catch {
        // Network rejection (offline/CORS) — treat as a dead session, never
        // retry: a retry could re-send a token the server already rotated.
        return killSession();
      }
    };

    const promise = run().finally(() => {
      refreshing.current = null;
    });
    refreshing.current = promise;
    return promise;
  };

  // The method the API layer calls before each request: hand back a token that
  // is still valid for at least EXPIRY_SKEW_MS, refreshing silently if not.
  const ensureFreshToken = async (): Promise<string | null> => {
    const current = sessionStorage.getItem(TOKEN_KEY);
    const refreshToken = sessionStorage.getItem(REFRESH_KEY);
    // Logged out and nothing to refresh from.
    if (!current && !refreshToken) return null;

    const claims = current ? decodeJwtClaims(current) : null;
    const expMs = typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
    const stale = expMs === null || expMs - Date.now() <= EXPIRY_SKEW_MS;
    // Fail closed: a stale token we can't refresh is never handed back — the
    // contract is "valid for ≥ EXPIRY_SKEW_MS or null", never a known-bad token.
    if (stale) return refreshToken ? refreshSession() : null;
    return current;
  };

  // Proactive refresh timer: keep an idle tab's session alive so background
  // react-query refetches never hit an expired token. Schedule a refresh at
  // (exp - EXPIRY_SKEW_MS); re-armed whenever `token` changes (a fresh token
  // resets the clock) and cleared on unmount. Background-tab throttling can
  // defer this setTimeout — the visibility top-up below recovers that case.
  useEffect(() => {
    if (!token) return;
    const claims = decodeJwtClaims(token);
    if (typeof claims?.exp !== 'number') return;
    const delay = Math.max(0, claims.exp * 1000 - Date.now() - EXPIRY_SKEW_MS);
    const id = setTimeout(() => {
      void refreshSession();
    }, delay);
    return () => clearTimeout(id);
    // refreshSession reads live storage and is single-flighted, so it's safe to
    // omit from deps; re-arming is driven solely by token rotation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Visibility top-up: when a backgrounded/slept tab becomes visible again, the
  // proactive setTimeout may have been throttled past expiry. Re-check on wake
  // and refresh if needed. Fire-and-forget; single-flight dedupes against any
  // in-flight proactive refresh.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void ensureFreshToken().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (): Promise<void> => {
    setError(null);
    const { verifier, challenge } = await generatePkce();
    const state = randomState();
    const redirectUri = window.location.origin + redirectPath;
    const pending: PendingAuth = { verifier, state, redirectUri };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    // Pass the management tenant so the minted token's `aud` matches what the
    // Watchtower Lambdas require.
    const authorizeUrl = buildAuthorizeUrl(config, { challenge, state, redirectUri });
    window.location.assign(authorizeUrl);
  };

  const signOut = (): void => {
    sessionStorage.removeItem(PENDING_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    setToken(null);
    setError(null);
    // Clear the server-denied latch so a subsequent sign-in re-evaluates access.
    setServerDenied(false);
  };

  const denyAccess = (): void => setServerDenied(true);

  const { address, canRead } = deriveState(token);

  const value: AuthContextValue = {
    config,
    token,
    address,
    canRead,
    serverDenied,
    denyAccess,
    error,
    signIn,
    signOut,
    ensureFreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** Strip the OAuth callback query (code/state) from the URL without a reload. */
function stripQuery(): void {
  window.history.replaceState({}, '', window.location.pathname + window.location.hash);
}
